/**
 * Tool and swatch tables for the annotation palette.
 *
 * Separate from the palette component so the editor can import the tool union
 * without pulling in React, and so the tables stay declarative (no literal
 * labels — `labelKey` is resolved with `t()` at render, which is what
 * `lint:i18n-const` requires of a `.map()`ed object array).
 */

/** `select` is the editing-only pointer tool; the rest map 1:1 to renderer kinds. */
export type AnnotationTool = 'select' | 'pen' | 'rect' | 'ellipse' | 'arrow' | 'text'

export interface AnnotationToolEntry {
  tool: AnnotationTool
  labelKey: string
}

export const ANNOTATION_TOOLS: readonly AnnotationToolEntry[] = [
  { tool: 'select', labelKey: 'annotate.tool.select' },
  { tool: 'pen', labelKey: 'annotate.tool.pen' },
  { tool: 'rect', labelKey: 'annotate.tool.rect' },
  { tool: 'ellipse', labelKey: 'annotate.tool.ellipse' },
  { tool: 'arrow', labelKey: 'annotate.tool.arrow' },
  { tool: 'text', labelKey: 'annotate.tool.text' },
]

/**
 * Swatches, `#RRGGBB` only — the renderer rejects any other colour form, so the
 * picker must never emit `rgba()` or a named colour.
 */
export const ANNOTATION_COLORS: readonly string[] = [
  '#ef4444',
  '#facc15',
  '#22c55e',
  '#38bdf8',
  '#a855f7',
  '#ffffff',
  '#000000',
]

export const DEFAULT_ANNOTATION_COLOR = '#ef4444'

/** Percent of `min(W, H)`. 1.5 lands near 5px on a 640x360 source. */
export const DEFAULT_STROKE_WIDTH_PCT = 1.5

/** Percent of `H`. 7 lands at 25px on a 360px-tall source. */
export const DEFAULT_FONT_SIZE_PCT = 7
export const MIN_FONT_SIZE_PCT = 2
export const MAX_FONT_SIZE_PCT = 20
export const FONT_SIZE_STEP_PCT = 0.5

/**
 * Minimum pointer travel, in percent, before a drag counts as a shape rather
 * than a click. Below this a rect/ellipse/arrow is discarded, so a stray click
 * with a shape tool selected does not litter the spec with zero-size elements.
 */
export const MIN_SHAPE_TRAVEL_PCT = 0.75

/**
 * Minimum spacing between recorded pen points, in percent. Without it a slow
 * drag records a point per pointermove event — thousands for one stroke, all of
 * which are POSTed and re-rendered.
 */
export const MIN_PEN_POINT_SPACING_PCT = 0.4

/** Maps a drawing tool to the renderer kind it authors. `select` draws nothing. */
export function toolToKind(tool: AnnotationTool): string | null {
  switch (tool) {
    case 'pen':
      return 'stroke'
    case 'rect':
      return 'rect'
    case 'ellipse':
      return 'ellipse'
    case 'arrow':
      return 'arrow'
    case 'text':
      return 'text'
    case 'select':
      return null
  }
}
